import type { LightboxState, LightboxElements, LightboxHandlers, LightboxImage } from './types';
import { ZOOM_STEP } from './state';
import { setZoom, resetZoom, updateImageTransform } from './zoom';
import { constrainPan, handleTouchStart, handleTouchMove, handleTouchEnd } from './pan';
import { goToImage } from './navigation';

export function bindElementEvents(
    state: LightboxState,
    elements: LightboxElements,
    handlers: LightboxHandlers
): void {
    elements.closeBtn.addEventListener('click', handlers.close);
    elements.backdrop.addEventListener('click', handlers.close);

    elements.prevBtn.addEventListener('click', () => {
        goToImage(state, elements, -1);
    });

    elements.nextBtn.addEventListener('click', () => {
        goToImage(state, elements, 1);
    });

    elements.zoomIn.addEventListener('click', () => {
        setZoom(state, elements, state.zoomLevel + ZOOM_STEP);
    });

    elements.zoomOut.addEventListener('click', () => {
        setZoom(state, elements, state.zoomLevel - ZOOM_STEP);
    });

    elements.zoomReset.addEventListener('click', () => {
        resetZoom(state, elements);
    });

    elements.img.addEventListener('dblclick', () => {
        if (state.zoomLevel > 1) {
            resetZoom(state, elements);
        } else {
            setZoom(state, elements, 2);
        }
    });

    elements.img.addEventListener('mousedown', (e: MouseEvent) => {
        if (state.zoomLevel <= 1) {
            return;
        }

        e.preventDefault();
        state.isDragging = true;
        state.startX = e.clientX - state.lastTranslateX;
        state.startY = e.clientY - state.lastTranslateY;
        elements.img.classList.add('is-dragging');
    });

    elements.content.addEventListener('wheel', (e: WheelEvent) => {
        if (!state.isOpen) {
            return;
        }

        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.25 : 0.25;
        setZoom(state, elements, state.zoomLevel + delta);
    }, { passive: false });

    elements.img.addEventListener('touchstart', (e: TouchEvent) => {
        handleTouchStart(state, elements, e);
    }, { passive: false });

    elements.img.addEventListener('touchmove', (e: TouchEvent) => {
        handleTouchMove(state, elements, e, (level) => {
            setZoom(state, elements, level);
        });
    }, { passive: false });

    elements.img.addEventListener('touchend', (e: TouchEvent) => {
        handleTouchEnd(state, elements, e);
    });
}

export function initDocumentListeners(
    state: LightboxState,
    elements: LightboxElements,
    handlers: LightboxHandlers
): void {
    document.addEventListener('mousemove', (e: MouseEvent) => {
        if (!state.isDragging) {
            return;
        }

        state.translateX = e.clientX - state.startX;
        state.translateY = e.clientY - state.startY;
        constrainPan(state, elements);
        updateImageTransform(state, elements);
    });

    document.addEventListener('mouseup', () => {
        if (!state.isDragging) {
            return;
        }

        state.isDragging = false;
        state.lastTranslateX = state.translateX;
        state.lastTranslateY = state.translateY;
        elements.img.classList.remove('is-dragging');
    });

    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (!state.isOpen) {
            return;
        }

        switch (e.key) {
            case 'Escape':
                handlers.close();
                break;
            case 'ArrowLeft':
                e.preventDefault();
                goToImage(state, elements, -1);
                break;
            case 'ArrowRight':
                e.preventDefault();
                goToImage(state, elements, 1);
                break;
            case '+':
            case '=':
                e.preventDefault();
                setZoom(state, elements, state.zoomLevel + ZOOM_STEP);
                break;
            case '-':
                e.preventDefault();
                setZoom(state, elements, state.zoomLevel - ZOOM_STEP);
                break;
            case '0':
                e.preventDefault();
                resetZoom(state, elements);
                break;
        }
    });
}

export function handleElementClick(
    e: Event,
    collectGroupImages: (groupName: string | null) => LightboxImage[],
    open: (images: LightboxImage[], startIndex: number) => void
): void {
    e.preventDefault();
    e.stopPropagation();

    const target = e.currentTarget as HTMLElement;
    const groupName = target.getAttribute('data-lightbox-group');
    const images = collectGroupImages(groupName);
    const startIndex = images.findIndex((img) => img.element === target);

    open(images, Math.max(0, startIndex));
}
